import React, { useRef, useEffect, useState, memo } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import styles from './MilestoneTooltip.module.scss';
import {
  formatEnhancedPopupText,
  ENHANCED_MILESTONE_STATUSES
} from '../../../components/SlideOutPanel/Milestones/milestoneConfigEnhanced';
import { Milestone } from '../types';
import { milestonesColumnsConfig } from '../TableConfig/milestonesColumnsConfig';
import { selectColumnConfig } from '../slice/selectors';

interface MilestoneTooltipProps {
  milestone: Milestone;
  cursorPos: { x: number; y: number; cellRight: number; cellLeft: number };
}

const MilestoneTooltip: React.FC<MilestoneTooltipProps> = memo(
  ({ milestone, cursorPos }) => {
    const tooltipRef = useRef<HTMLDivElement>(null);
    const apiColumnConfig = useSelector(selectColumnConfig);
    const effectiveColumnsConfig =
      apiColumnConfig && apiColumnConfig.length > 0
        ? apiColumnConfig
        : (milestonesColumnsConfig as any[]);
    const [shouldFlipToTop, setShouldFlipToTop] = useState(false);
    const [shouldFlipToLeft, setShouldFlipToLeft] = useState(false);
    const [tooltipXPosition, setTooltipXPosition] = useState(0);
    const [isMovedToCellLeft, setIsMovedToCellLeft] = useState(false);

    // Check if tooltip would overflow viewport and flip if needed
    useEffect(() => {
      if (tooltipRef.current) {
        const tooltipHeight = tooltipRef.current.offsetHeight;
        const tooltipWidth = tooltipRef.current.offsetWidth;
        const tooltipBottom = cursorPos.y + 10 + tooltipHeight; // 10px offset from cursor
        const viewportHeight = window.innerHeight;
        const viewportWidth = window.innerWidth;
        const safetyMargin = 20;

        // Check vertical overflow
        const shouldFlip = tooltipBottom > viewportHeight;
        setShouldFlipToTop(shouldFlip);

        // Check if the cell itself is beyond viewport
        const isCellBeyondViewport = cursorPos.cellRight > viewportWidth;

        // Check horizontal overflow
        // If cell is beyond viewport, flip immediately
        // Otherwise check if centered tooltip would overflow
        const tooltipRight = cursorPos.x + tooltipWidth * 0.4;
        const needsFlipToLeft =
          isCellBeyondViewport || tooltipRight > viewportWidth - safetyMargin;

        // NEW LOGIC: Check if tooltip would overflow viewport by more than 100px
        // If yes, move tooltip to cell's left edge (not midpoint)
        const overflowAmount = tooltipRight - viewportWidth;
        const shouldMoveToCellLeft = overflowAmount > 100;

        let finalXPos = cursorPos.x;
        if (shouldMoveToCellLeft) {
          finalXPos = cursorPos.cellLeft;
        }

        setIsMovedToCellLeft(shouldMoveToCellLeft);

        setTooltipXPosition(finalXPos);
        setShouldFlipToLeft(needsFlipToLeft);
      }
    }, [cursorPos.x, cursorPos.y, cursorPos.cellRight, cursorPos.cellLeft]);
    const reviewType = milestone.reviewType ?? undefined;

    // Get approval required status from milestoneAttributes or fall back to column config
    const getApprovalRequired = (): boolean => {
      // First, check milestoneAttributes if it exists
      if (milestone.milestoneAttributes?.approvalRequired !== undefined) {
        return milestone.milestoneAttributes.approvalRequired;
      }

      // Fall back to column configuration based on milestone name
      const columnConfig = effectiveColumnsConfig.find(
        (col: any) => col.milestoneName === milestone.name
      );
      return columnConfig?.approvalRequired ?? false;
    };

    const isApprovalRequired = getApprovalRequired();

    // Generate enhanced popup text (same logic as slideout panel)
    // Convert null to undefined to match expected types
    const enhancedPopupText = formatEnhancedPopupText(
      (milestone.status ?? '') as string,
      isApprovalRequired,
      reviewType,
      milestone.date ?? undefined,
      milestone.meetingScheduledAt ?? undefined,
      milestone.secondaryDate ?? undefined,
      milestone.approver ?? milestone.approvedBy ?? undefined
    );

    // Calculate arrow position when flipped to left (as percentage from right)
    // When moved to cell left, use a smaller offset to move arrow more to the right
    const arrowRightPosition =
      tooltipRef.current && shouldFlipToLeft
        ? `${
            tooltipRef.current.offsetWidth * (isMovedToCellLeft ? 0.01 : 0.05)
          }px`
        : undefined;

    const tooltipContent = (
      <div
        ref={tooltipRef}
        className={`${styles.hoverPopup} ${
          shouldFlipToTop ? styles.flippedToTop : ''
        } ${shouldFlipToLeft ? styles.flippedToLeft : ''}`}
        style={{
          top: isMovedToCellLeft ? cursorPos.y - 5 : cursorPos.y,
          left: tooltipXPosition,
          ...(shouldFlipToLeft &&
            arrowRightPosition &&
            ({ '--arrow-right-pos': arrowRightPosition } as any))
        }}
      >
        <div className={styles.arrow}></div>
        <div className={styles.tooltipContainer}>
          <div className={styles.tooltipContent}>
            <div className={styles.titleRow}>
              <div className={styles.milestoneTitle}>
                <span className={styles.milestoneName}>{milestone.name}</span>
                <div className={styles.titleIndicators}>
                  {isApprovalRequired && (
                    <span className={styles.approvalIndicator}>A</span>
                  )}
                  {isApprovalRequired && (
                    <span className={styles.approvalText}>
                      (Approval Required)
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Tags and Enhanced Popup Text */}
            <div className={styles.contentSection}>
              {/* Tags Section - Review Type and Status */}
              <div className={styles.tagsSection}>
                {reviewType && <div className={styles.tag}>{reviewType}</div>}
                {milestone.status && (
                  <div className={styles.tag}>
                    {(() => {
                      // Get the display label from configuration
                      if (
                        !milestone.status ||
                        typeof milestone.status !== 'string'
                      ) {
                        return milestone.status || 'TBD';
                      }
                      const statusKey = milestone.status.toUpperCase();
                      const statusConfig =
                        ENHANCED_MILESTONE_STATUSES[statusKey];
                      return statusConfig?.label || milestone.status;
                    })()}
                  </div>
                )}
              </div>

              {/* Rollover Copy Text below tags */}
              {enhancedPopupText && enhancedPopupText.trim() && (
                <div className={styles.rolloverText}>
                  {enhancedPopupText.split('\n').map((line, index) => {
                    // First try to split by "on" (e.g., "Meeting Scheduled on Feb 19, 2025")
                    const onIndex = line.indexOf(' on ');
                    if (onIndex !== -1) {
                      const label = line
                        .substring(0, onIndex + 3) // Include "on" with the label
                        .trim();
                      const value = line.substring(onIndex + 4).trim(); // Everything after "on "
                      return (
                        <div key={index} className={styles.rolloverLine}>
                          <span className={styles.rolloverLabel}>{label}</span>
                          <span className={styles.rolloverValue}>{value}</span>
                        </div>
                      );
                    }

                    // Fallback: Split on colon to separate label and value like "Sent on: Feb 19, 2025"
                    const colonIndex = line.indexOf(':');
                    if (colonIndex !== -1) {
                      const label = line.substring(0, colonIndex + 1).trim();
                      const value = line.substring(colonIndex + 1).trim();
                      return (
                        <div key={index} className={styles.rolloverLine}>
                          <span className={styles.rolloverLabel}>{label} </span>
                          <span className={styles.rolloverValue}>{value}</span>
                        </div>
                      );
                    } else {
                      return (
                        <div key={index} className={styles.rolloverLine}>
                          {line}
                        </div>
                      );
                    }
                  })}

                  {/* ⭐ Show "Approved By" ONLY if status config indicates showApprover: true */}
                  {(() => {
                    // Check status configuration to see if approver should be shown
                    const statusConfig =
                      milestone.status && typeof milestone.status === 'string'
                        ? ENHANCED_MILESTONE_STATUSES[
                            milestone.status.toUpperCase()
                          ]
                        : null;

                    if (!statusConfig?.showApprover) return null;

                    // Once concluded (Approved/Re-Approved/Not Approved), show who
                    // actually acted instead of the pool of designated approvers —
                    // mirrors MilestoneHoverPopup's `approverResolution` so both
                    // tooltips agree on who gets shown and how it's labeled.
                    const statusLower = (milestone.status || '')
                      .toLowerCase()
                      .replace(/_async$/, '');
                    const approverResolution =
                      statusLower === 're_approved'
                        ? {
                            label: 'Re-Approved By:',
                            value: milestone.reApprovedBy || ''
                          }
                        : statusLower === 'approved'
                        ? {
                            label: 'Approved By:',
                            value: milestone.approvedBy || ''
                          }
                        : statusLower === 'not_approved'
                        ? {
                            label: 'Rejected By:',
                            value: milestone.notApprovedBy || ''
                          }
                        : null;

                    if (approverResolution) {
                      return approverResolution.value ? (
                        <div className={styles.rolloverLine}>
                          <span className={styles.rolloverLabel}>
                            {approverResolution.label}{' '}
                          </span>
                          <span className={styles.rolloverValue}>
                            {approverResolution.value}
                          </span>
                        </div>
                      ) : null;
                    }

                    // Not concluded (e.g. Sent, reopened after a prior cycle) — the
                    // live/current assignment takes priority over a stale `approvedBy`
                    // left behind by an earlier, now-superseded approval cycle.
                    const shouldShowApprover =
                      milestone.approver || milestone.approvedBy;

                    return shouldShowApprover ? (
                      <div className={styles.rolloverLine}>
                        <span className={styles.rolloverLabel}>Approver: </span>
                        <span className={styles.rolloverValue}>
                          {milestone.approver || milestone.approvedBy}
                        </span>
                      </div>
                    ) : null;
                  })()}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );

    // Render using portal to the tooltip container
    const tooltipContainer = document.body;
    if (!tooltipContainer) return null;

    return createPortal(tooltipContent, tooltipContainer);
  }
);

MilestoneTooltip.displayName = 'MilestoneTooltip';

export default MilestoneTooltip;
